import type { CSSProperties, HTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";

/**
 * Font-size (px) drives the whole cluster: each dot is `0.5em` and gaps scale
 * with it, so a single `size` token controls the footprint. Kept small — the
 * meatballs loader is the compact default behind <Spinner />.
 */
const fontSizes = {
	sm: 9,
	default: 11,
	md: 14,
	lg: 18,
} as const;

const speeds = {
	slow: "1.5s",
	normal: "1.1s",
	fast: "0.8s",
} as const;

export interface MeatballsLoaderProps
	extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
	size?: keyof typeof fontSizes;
	speed?: keyof typeof speeds;
}

/**
 * Compact loading indicator: three tightly-packed dots pulsing in a staggered
 * wave. Color inherits from the parent's `text-*` via `bg-current`. Structural
 * classes come last in `cn` so consumer width/height utilities (e.g. `size-4`)
 * can't clip the horizontal cluster — pass `text-*` for color and margins for
 * spacing; use the `size` prop to scale.
 *
 * @example
 * <MeatballsLoader />
 * <MeatballsLoader size="md" className="text-primary" />
 */
export function MeatballsLoader({
	size = "default",
	speed = "normal",
	className,
	style,
	...props
}: MeatballsLoaderProps) {
	return (
		<span
			aria-label="Loading"
			className={cn(
				className,
				"inline-flex w-fit items-center justify-center gap-[0.28em] align-middle leading-none",
			)}
			data-slot="spinner"
			role="status"
			style={{ fontSize: fontSizes[size], ...style } as CSSProperties}
			{...props}
		>
			{[0, 1, 2].map((i) => (
				<span
					aria-hidden="true"
					className="inline-block size-[0.5em] animate-meatball rounded-full bg-current"
					key={i}
					style={
						{
							"--meatball-duration": speeds[speed],
							"--meatball-delay": `${i * 140}ms`,
						} as CSSProperties
					}
				/>
			))}
		</span>
	);
}
